/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { XMLParser } from 'fast-xml-parser'; // Correctly import XMLParser
import { Store } from '@ngrx/store';
import { AppState } from '@app/Store/reducers';
import { iEdmx } from '@app/Shared/interfaces/iEntity';
import { parseEdmxData, parseTruemetadata } from '@app/Shared/util/parse';
import { environment } from '@environments/environment';
import { iButtonItem } from '@app/Shared/interfaces/iMenu';
import { AddParentChildIn } from '@app/Shared/models/add-parent-in.model';
import { RemoveParentChildIn } from '@app/Shared/models/remove-parent-in.model';
import { ChangeParentChildLinkKey } from '@app/Shared/models/change-parent-child-link-key.model';
import { ReassignMatch } from '@app/Shared/models/reassign-match.model';
import { OverrideHistoryIn } from '@app/Shared/models/override-history-in.model';
import { ReturnSurvivorshipIn } from '@app/Shared/models/return-survivorship-in.model';
import { pluralize } from '@app/Shared/util/stringManipulation';
import { ODataBatchOperation } from '@app/Shared/interfaces/iODataBatchOperation';
import { Router } from '@angular/router';

const { oData: baseUrl, api: apiUrl, metadata: metadataUrl } = environment.baseUrls;
const TRUEMETADATA_URL = `${metadataUrl}`;

/** OData entity sets that use a Pre- prefix for ingest-to-master mapping UI. */
const CONFIG_PRE_ENTITY_SETS = new Set([
    'DataFieldRules',
    'DataFieldRuleLinks',
    'DataFieldRuleTypes',
    'IngestDataFields',
    'IngestDataSets',
    'IngestDataSetRltnshps',
    'IngestFiles',
    'IngestMasterLinks',
    'IngestStatMaps',
    'MasterDataFields',
    'MasterTables',
    'MasterTableRelations',
    'WorkflowTasks',
    'WorkflowTypes',
    'Srcs',
]);

@Injectable({
    providedIn: 'root',
})
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export class OdataService {
    constructor(
        protected http: HttpClient,
        private store: Store<AppState>,
        private router: Router,
    ) {}

    private isConfigurationRoute(): boolean {
        return this.router.url.includes('/configuration');
    }

    private entitySetForRequest(entitySet: string): string {
        return this.isConfigurationRoute() && CONFIG_PRE_ENTITY_SETS.has(entitySet) ? `Pre${entitySet}` : entitySet;
    }

    /** Navigation / $expand property names also get Pre- on configuration. */
    private navProp(name: string): string {
        return this.isConfigurationRoute() ? `Pre${name}` : name;
    }

    private mapBatchResource(resource: string): string {
        return resource.replace(/^([A-Za-z]+)(\(.*)?$/, (_, entitySet, rest) => `${this.entitySetForRequest(entitySet)}${rest ?? ''}`);
    }

    /** Rewrite OData $filter/$orderby navigation paths for Pre- entity sets. */
    private rewriteNavPaths(query?: string): string | undefined {
        if (!query || !this.isConfigurationRoute()) {
            return query;
        }
        const navs = ['IngestDataField', 'IngestDataSet', 'IngestFile', 'MasterDataField', 'MasterTable'];
        let result = query;
        for (const nav of navs) {
            result = result.replace(new RegExp(`(?<!Pre)${nav}\\b`, 'g'), `Pre${nav}`);
        }
        return result;
    }
    /** Map Pre* expand properties back to original names for existing UI bindings. */
    private normalizeExpandedRow(row: any): any {
        if (row == null || typeof row !== 'object') {
            return row;
        }
        if (Array.isArray(row)) {
            return row.map((item) => this.normalizeExpandedRow(item));
        }

        const aliases: Record<string, string> = {
            PreIngestFile: 'IngestFile',
            PreIngestDataField: 'IngestDataField',
            PreIngestDataSet: 'IngestDataSet',
            PreMasterDataField: 'MasterDataField',
            PreMasterTable: 'MasterTable',
            PreSrc: 'Src',
        };

        const out: Record<string, any> = { ...row };
        for (const [preKey, plainKey] of Object.entries(aliases)) {
            if (out[preKey] != null) {
                out[plainKey] = this.normalizeExpandedRow(out[preKey]);
            } else if (out[plainKey] != null) {
                out[plainKey] = this.normalizeExpandedRow(out[plainKey]);
            }
        }
        return out;
    }
    getMetaData(query: string): Observable<any> {
        return this.http.get<any[]>(TRUEMETADATA_URL).pipe(
            map((data) => parseTruemetadata(data)),
            catchError((error) => {
                console.error('[OdataService] Truemetadata API error:', error?.message);
                return throwError(() => error);
            }),
        );
    }

    private parseXmlToJs(xmlString: string): iEdmx {
        try {
            const xmlParser = new XMLParser({
                ignoreAttributes: false,
                parseAttributeValue: true,
                attributeNamePrefix: '',
            });
            const parsed = xmlParser.parse(xmlString);

            // Use optional chaining to simplify the check
            if (parsed?.['edmx:Edmx']?.['edmx:DataServices']?.Schema) {
                return parsed;
            } else {
                throw new Error('Invalid XML format');
            }
        } catch (error) {
            throw new Error('Invalid XML format');
        }
    }

    addParentChild(model: AddParentChildIn): Observable<any> {
        const url: string = `${apiUrl}link/${model.masterTable}/${model.masterKey}/${model.linkTable}?relationship=${model.relationship}&newRelationKey=${model.newRelationKey}`;
        return this.http.post(url, null, { headers: { 'skip-error-handler': 'true' } }).pipe(map((response: any) => response));
    }

    changeParentChildLinkKey(model: ChangeParentChildLinkKey): Observable<any> {
        const url: string = `${apiUrl}link/${model.masterTable}/${model.masterKey}/${model.linkTable}/${model.linkKey}?newMasterKey=${model.newMasterKey}&relationship=${model.relationship}`;
        return this.http.put(url, null, { headers: { 'skip-error-handler': 'true' } }).pipe(map((response: any) => response));
    }

    removeParentChild(model: RemoveParentChildIn): Observable<any> {
        const url: string = `${apiUrl}link/${model.masterTable}/${model.masterKey}/${model.linkTable}/${model.linkKey}`;
        return this.http.delete(url, { headers: { 'skip-error-handler': 'true' } }).pipe(map((response: any) => response));
    }
    reassignMatch(model: ReassignMatch): Observable<any> {
        const url: string = `${apiUrl}match/${model.masterTable}/${model.masterKey}/${model.datasetName}/${model.matchKey}?newMasterKey=${model.newMasterKey}`;
        return this.http.put(url, null, { headers: { 'skip-error-handler': 'true' } }).pipe(map((response: any) => response));
    }

    returnSurvivorship(model: ReturnSurvivorshipIn): Observable<any> {
        const url: string = `${apiUrl}merge/${model.masterTable}/${model.masterKey}/${model.historyKey}`;
        return this.http.delete(url, { headers: { 'skip-error-handler': 'true' } }).pipe(map((response: any) => response));
    }

    overrideHistory(model: OverrideHistoryIn): Observable<any> {
        const url: string = `${apiUrl}merge/${model.masterTable}/${model.masterKey}/${model.historyKey}?newFieldValue=${model.newFieldValue}`;
        return this.http.patch(url, null, { headers: { 'skip-error-handler': 'true' } }).pipe(map((response: any) => response));
    }

    /**
     * GET {{baseURL}}/OData/v1/IngestDataSets
     */
    getIngestDataSets(): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('IngestDataSets')}?$expand=${this.navProp('IngestFile')}`;
        return this.http.get<any>(url).pipe(
            map((res: any) => {
                if (Array.isArray(res?.value)) {
                    return { ...res, value: res.value.map((row: any) => this.normalizeExpandedRow(row)) };
                }
                return this.normalizeExpandedRow(res);
            }),
            catchError((error) => {
                console.error('[ERROR]: Get IngestDataSets Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    /**
     * GET {{baseURL}}/OData/v1/IngestDataSets?$expand=IngestFile&$top=&$skip=&$count=true
     */
    getIngestDataSetsPaged(top: number, skip: number): Observable<{ value: any[]; count: number }> {
        const url = `${baseUrl}${this.entitySetForRequest('IngestDataSets')}?$expand=${this.navProp('IngestFile')}&$top=${top}&$skip=${skip}&$count=true`;
        return this.http.get<any>(url).pipe(
            map((res: any) => ({
                value: (res?.value ?? []).map((row: any) => this.normalizeExpandedRow(row)),
                count: Number(res?.['@odata.count'] ?? 0),
            })),
            catchError((error) => {
                console.error('[ERROR]: Get IngestDataSetsPaged Error:', error.message);
                return throwError(() => error);
            }),
        );
    }
    /**
     * GET {{baseURL}}/OData/v1/IngestFiles
     */
    getIngestFiles(): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('IngestFiles')}`;
        return this.http.get<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Get IngestFiles Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    /**
     * GET {{baseURL}}/OData/v1/IngestDataFields
     */
    getIngestDataFields(): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('IngestDataFields')}`;
        return this.http.get<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Get IngestDataFields Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    getIngestDataFieldsByDatasetKey(ingestDataSetKey: number): Observable<{ value: any[] }> {
        const url = `${baseUrl}${this.entitySetForRequest('IngestDataFields')}?$filter=IngestDataSetKey eq ${ingestDataSetKey}&$top=500`;
        return this.http.get<any>(url).pipe(
            map((res: any) => ({ value: res?.value ?? [] })),
            catchError((error) => {
                console.error('[ERROR]: Get IngestDataFieldsByDatasetKey Error:', error.message);
                return throwError(() => error);
            }),
        );
    }
    getIngestDataFieldsByKeys(fieldKeys: number[]): Observable<{ value: any[] }> {
        if (fieldKeys.length === 0) {
            return of({ value: [] });
        }
        const filter = fieldKeys.map((k) => `IngestDataFieldKey eq ${k}`).join(' or ');
        const url = `${baseUrl}${this.entitySetForRequest('IngestDataFields')}?$filter=${filter}`;
        return this.http.get<any>(url).pipe(
            map((res: any) => ({ value: res?.value ?? [] })),
            catchError((error) => {
                console.error('[ERROR]: Get IngestDataFieldsByKeys Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    getIngestDataFieldsPaged(top: number, skip: number, ingestDataSetKey: number): Observable<{ value: any[]; count: number }> {
        const url = `${baseUrl}${this.entitySetForRequest('IngestDataFields')}?$filter=IngestDataSetKey eq ${ingestDataSetKey}&$top=${top}&$skip=${skip}&$count=true`;
        return this.http.get<any>(url).pipe(
            map((res: any) => ({
                value: res?.value ?? [],
                count: Number(res?.['@odata.count'] ?? 0),
            })),
            catchError((error) => {
                console.error('[ERROR]: Get IngestDataFieldsPaged Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    /**
     * GET {{baseURL}}/OData/v1/IngestStatMaps
     */
    getIngestStatMaps(): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('IngestStatMaps')}`;
        return this.http.get<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Get IngestStatMaps Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    getIngestStatMapsPaged(top: number, skip: number, ingestDataFieldKey: number): Observable<{ value: any[]; count: number }> {
        const url = `${baseUrl}${this.entitySetForRequest('IngestStatMaps')}?$filter=IngestDataFieldKey eq ${ingestDataFieldKey}&$top=${top}&$skip=${skip}&$count=true`;
        return this.http.get<any>(url).pipe(
            map((res: any) => ({
                value: res?.value ?? [],
                count: Number(res?.['@odata.count'] ?? 0),
            })),
            catchError((error) => {
                console.error('[ERROR]: Get IngestStatMapsPaged Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    /**
     * GET {{baseURL}}/OData/v1/MasterDataFields
     */
    getMasterDataFields(): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('MasterDataFields')}`;
        return this.http.get<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Get MasterDataFields Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    getMasterDataFieldsPaged(top: number, skip: number, masterTableKey: number): Observable<{ value: any[]; count: number }> {
        const url = `${baseUrl}${this.entitySetForRequest('MasterDataFields')}?$filter=MasterTableKey eq ${masterTableKey}&$top=${top}&$skip=${skip}&$count=true`;
        return this.http.get<any>(url).pipe(
            map((res: any) => ({
                value: res?.value ?? [],
                count: Number(res?.['@odata.count'] ?? 0),
            })),
            catchError((error) => {
                console.error('[ERROR]: Get MasterDataFieldsPaged Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    getMasterDataFieldsByTableKey(masterTableKey: number): Observable<{ value: any[] }> {
        const url = `${baseUrl}${this.entitySetForRequest('MasterDataFields')}?$filter=MasterTableKey eq ${masterTableKey}&$top=500`;
        return this.http.get<any>(url).pipe(
            map((res: any) => ({ value: res?.value ?? [] })),
            catchError((error) => {
                console.error('[ERROR]: Get MasterDataFieldsByTableKey Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    /**
     * GET {{baseURL}}/OData/v1/MasterTables
     */
    getMasterTables(): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('MasterTables')}`;
        return this.http.get<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Get MasterTables Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    getMasterTablesPaged(top: number, skip: number): Observable<{ value: any[]; count: number }> {
        const url = `${baseUrl}${this.entitySetForRequest('MasterTables')}?$top=${top}&$skip=${skip}&$count=true`;
        return this.http.get<any>(url).pipe(
            map((res: any) => ({
                value: res?.value ?? [],
                count: Number(res?.['@odata.count'] ?? 0),
            })),
            catchError((error) => {
                console.error('[ERROR]: Get MasterTablesPaged Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    /**
     * GET {{baseURL}}/OData/v1/IngestDataSetRltnshps with server-side paging/filter/sort
     */
    getIngestRelationshipsPaged(top: number, skip: number, filter?: string, orderby?: string): Observable<{ value: any[]; count: number }> {
        const ingestDataField = this.navProp('IngestDataField');
        const ingestDataSet = this.navProp('IngestDataSet');
        const masterDataField = this.navProp('MasterDataField');
        const masterTable = this.navProp('MasterTable');
        const expand =
            `${ingestDataField}($expand=${ingestDataSet}($select=IngestDataSetNm,IngestDataSetKey,SrcTrukey);$select=IngestDataFieldNm,IngestDataFieldKey),` +
            `${masterDataField}($expand=${masterTable}($select=MasterTableNm,MasterTableOdataNm,MasterTableKey);$select=MasterDataFieldNm,MasterDataFieldKey,MasterDataFieldOdataNm),` +
            `${masterTable}($select=MasterTableNm,MasterTableKey,MasterTableOdataNm,MasterTableType)`;

        const queryParts = [`$expand=${expand}`, `$top=${top}`, `$skip=${skip}`, '$count=true'];
        if (orderby) {
            queryParts.push(`$orderby=${this.rewriteNavPaths(orderby)}`);
        }
        if (filter) {
            queryParts.push(`$filter=${this.rewriteNavPaths(filter)}`);
        }

        const url = `${baseUrl}${this.entitySetForRequest('IngestDataSetRltnshps')}?${queryParts.join('&')}`;
        return this.http.get<any>(url).pipe(
            map((res: any) => ({
                value: (res?.value ?? []).map((row: any) => this.normalizeExpandedRow(row)),
                count: Number(res?.['@odata.count'] ?? 0),
            })),
            catchError((error) => {
                console.error('[ERROR]: Get IngestDataSetRltnshps Error:', error.message);
                return throwError(() => error);
            }),
        );
    }
    createIngestRelationship(
        data: {
            IngestDataFieldKey: number;
            MasterDataFieldKey: number;
            BaseMasterTableKey: number;
            BaseMasterTableOdataNm: string;
            BaseMasterDataFieldKey: number;
            BaseMasterDataFieldOdataNm: string;
            IngestDataSetRltnshpType: string;
            [key: string]: any;
        },
        key?: number,
    ): Observable<any> {
        return this.postWithEntityKey('IngestDataSetRltnshps', 'IngestDataSetRltnshpKey', data, key).pipe(
            catchError((error) => {
                console.error('[ERROR]: Create IngestRelationship Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    updateIngestRelationship(
        key: number,
        data: {
            IngestDataFieldKey?: number;
            MasterDataFieldKey?: number;
            BaseMasterTableKey?: number;
            BaseMasterTableOdataNm?: string;
            BaseMasterDataFieldKey?: number;
            BaseMasterDataFieldOdataNm?: string;
            IngestDataSetRltnshpType?: string;
            [key: string]: any;
        },
    ): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('IngestDataSetRltnshps')}(${key})`;
        return this.http.patch<any>(url, data).pipe(
            catchError((error) => {
                console.error('[ERROR]: Update IngestRelationship Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    deleteIngestRelationship(key: number): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('IngestDataSetRltnshps')}(${key})`;
        return this.http.delete<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Delete IngestRelationship Error:', error.message);
                return throwError(() => error);
            }),
        );
    }
    getMasterTableRelationsPaged(top: number, skip: number, filter?: string, orderby?: string): Observable<{ value: any[]; count: number }> {
        const expand = `${this.navProp('MasterTable')}($select=MasterTableNm,MasterTableKey,MasterTableOdataNm)`;
        const queryParts = [`$expand=${expand}`, `$top=${top}`, `$skip=${skip}`, '$count=true'];
        if (orderby) {
            queryParts.push(`$orderby=${this.rewriteNavPaths(orderby)}`);
        }
        if (filter) {
            queryParts.push(`$filter=${this.rewriteNavPaths(filter)}`);
        }

        const url = `${baseUrl}${this.entitySetForRequest('MasterTableRelations')}?${queryParts.join('&')}`;
        return this.http.get<any>(url).pipe(
            map((res: any) => ({
                value: (res?.value ?? []).map((row: any) => this.normalizeExpandedRow(row)),
                count: Number(res?.['@odata.count'] ?? 0),
            })),
            catchError((error) => {
                console.error('[ERROR]: Get MasterTableRelations Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    createMasterTableRelation(
        data: {
            MasterTableKey: number;
            RelationType: string;
            MasterTableOdataNm: string;
            MasterKeyOdataNm: string;
            RelationTableOdataNm: string;
            RelationKeyOdataNm?: string;
            LinkTableOdataNm?: string;
            LinkKeyOdataNm?: string;
            [key: string]: any;
        },
        key?: number,
    ): Observable<any> {
        return this.postWithEntityKey('MasterTableRelations', 'MasterTableRelationKey', data, key).pipe(
            catchError((error) => {
                console.error('[ERROR]: Create MasterTableRelation Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    updateMasterTableRelation(
        key: number,
        data: {
            MasterTableKey?: number;
            RelationType?: string;
            MasterTableOdataNm?: string;
            MasterKeyOdataNm?: string;
            RelationTableOdataNm?: string;
            RelationKeyOdataNm?: string;
            LinkTableOdataNm?: string;
            LinkKeyOdataNm?: string;
            [key: string]: any;
        },
    ): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('MasterTableRelations')}(${key})`;
        return this.http.patch<any>(url, data).pipe(
            catchError((error) => {
                console.error('[ERROR]: Update MasterTableRelation Error:', error.message);
                return throwError(() => error);
            }),
        );
    }
    deleteMasterTableRelation(key: number): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('MasterTableRelations')}(${key})`;
        return this.http.delete<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Delete MasterTableRelation Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    /** IngestDataSets CRUD */
    createIngestDataSet(data: { IngestDataSetNm: string; IngestFileKey: number; SrcTrukey: number; [key: string]: any }, key?: number): Observable<any> {
        return this.postWithEntityKey('IngestDataSets', 'IngestDataSetKey', data, key).pipe(
            catchError((error) => {
                console.error('[ERROR]: Create IngestDataSet Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    /** IngestFiles CRUD */
    createIngestFile(
        data: {
            IngestFileNm: string;
            IngestFileProviderNm?: string;
            IngestFileFormatTxt?: string;
            IngestFileLocationDesc?: string;
            [key: string]: any;
        },
        key?: number,
    ): Observable<any> {
        return this.postWithEntityKey('IngestFiles', 'IngestFileKey', data, key).pipe(
            catchError((error) => {
                console.error('[ERROR]: Create IngestFile Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    updateIngestFile(
        key: number,
        data: {
            IngestFileNm?: string;
            IngestFileProviderNm?: string;
            IngestFileFormatTxt?: string;
            IngestFileLocationDesc?: string;
            [key: string]: any;
        },
    ): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('IngestFiles')}(${key})`;
        return this.http.patch<any>(url, data).pipe(
            catchError((error) => {
                console.error('[ERROR]: Update IngestFile Error:', error.message);
                return throwError(() => error);
            }),
        );
    }
    deleteIngestFile(key: number): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('IngestFiles')}(${key})`;
        return this.http.delete<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Delete IngestFile Error:', error.message);
                return throwError(() => error);
            }),
        );
    }
    updateIngestDataSet(key: number, data: { IngestDataSetNm?: string; IngestFileKey?: number; SrcTrukey?: number; [key: string]: any }): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('IngestDataSets')}(${key})`;
        return this.http.patch<any>(url, data).pipe(
            catchError((error) => {
                console.error('[ERROR]: Update IngestDataSet Error:', error.message);
                return throwError(() => error);
            }),
        );
    }
    deleteIngestDataSet(key: number): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('IngestDataSets')}(${key})`;
        return this.http.delete<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Delete IngestDataSet Error:', error.message);
                return throwError(() => error);
            }),
        );
    }
    /** IngestDataFields CRUD */
    createIngestDataField(
        data: {
            IngestDataFieldNm: string;
            IngestDataSetKey: number;
            IngestDataFieldType?: string;
            IngestDataFieldNullableFlg?: boolean;
            [key: string]: any;
        },
        key?: number,
    ): Observable<any> {
        return this.postWithEntityKey('IngestDataFields', 'IngestDataFieldKey', data, key).pipe(
            catchError((error) => {
                console.error('[ERROR]: Create IngestDataField Error:', error.message);
                return throwError(() => error);
            }),
        );
    }
    updateIngestDataField(
        key: number,
        data: { IngestDataFieldNm?: string; IngestDataSetKey?: number; IngestDataFieldType?: string; IngestDataFieldNullableFlg?: boolean; [key: string]: any },
    ): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('IngestDataFields')}(${key})`;
        return this.http.patch<any>(url, data).pipe(
            catchError((error) => {
                console.error('[ERROR]: Update IngestDataField Error:', error.message);
                return throwError(() => error);
            }),
        );
    }
    deleteIngestDataField(key: number): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('IngestDataFields')}(${key})`;
        return this.http.delete<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Delete IngestDataField Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    /** IngestStatMaps CRUD */
    createIngestStatMap(
        data: {
            IngestSrcStat: string;
            IngestStdStat: string;
            IngestMstrPrcssInd: string;
            IngestStatPriorityLvl: number;
            IngestDataFieldKey: number;
            [key: string]: any;
        },
        key?: number,
    ): Observable<any> {
        return this.postWithEntityKey('IngestStatMaps', 'IngestStatMapKey', data, key).pipe(
            catchError((error) => {
                console.error('[ERROR]: Create IngestStatMap Error:', error.message);
                return throwError(() => error);
            }),
        );
    }
    updateIngestStatMap(
        key: number,
        data: { IngestSrcStat?: string; IngestStdStat?: string; IngestMstrPrcssInd?: string; IngestStatPriorityLvl?: number; IngestDataFieldKey?: number; [key: string]: any },
    ): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('IngestStatMaps')}(${key})`;
        return this.http.patch<any>(url, data).pipe(
            catchError((error) => {
                console.error('[ERROR]: Update IngestStatMap Error:', error.message);
                return throwError(() => error);
            }),
        );
    }
    deleteIngestStatMap(key: number): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('IngestStatMaps')}(${key})`;
        return this.http.delete<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Delete IngestStatMap Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    /** MasterTables CRUD */
    createMasterTable(
        data: {
            MasterTableNm: string;
            MasterTableOdataNm?: string;
            MasterTableUiLabelTxt?: string;
            MasterTableType?: string;
            MasterTableDomainNm?: string;
            [key: string]: any;
        },
        key?: number,
    ): Observable<any> {
        return this.postWithEntityKey('MasterTables', 'MasterTableKey', data, key).pipe(
            catchError((error) => {
                console.error('[ERROR]: Create MasterTable Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    updateMasterTable(
        key: number,
        data: {
            MasterTableNm?: string;
            MasterTableOdataNm?: string;
            MasterTableUiLabelTxt?: string;
            MasterTableType?: string;
            MasterTableDomainNm?: string;
            [key: string]: any;
        },
    ): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('MasterTables')}(${key})`;
        return this.http.patch<any>(url, data).pipe(
            catchError((error) => {
                console.error('[ERROR]: Update MasterTable Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    deleteMasterTable(key: number): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('MasterTables')}(${key})`;
        return this.http.delete<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Delete MasterTable Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    /** MasterDataFields CRUD */
    createMasterDataField(
        data: {
            MasterDataFieldNm: string;
            MasterTableKey: number;
            MasterDataFieldOdataNm?: string;
            MasterDataFieldUiLabelTxt?: string;
            MasterDataFieldType?: string;
            MasterDataFieldReadOnly?: boolean;
            [key: string]: any;
        },
        key?: number,
    ): Observable<any> {
        return this.postWithEntityKey('MasterDataFields', 'MasterDataFieldKey', data, key).pipe(
            catchError((error) => {
                console.error('[ERROR]: Create MasterDataField Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    updateMasterDataField(
        key: number,
        data: {
            MasterDataFieldNm?: string;
            MasterTableKey?: number;
            MasterDataFieldOdataNm?: string;
            MasterDataFieldUiLabelTxt?: string;
            MasterDataFieldType?: string;
            MasterDataFieldReadOnly?: boolean;
            [key: string]: any;
        },
    ): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('MasterDataFields')}(${key})`;
        return this.http.patch<any>(url, data).pipe(
            catchError((error) => {
                console.error('[ERROR]: Update MasterDataField Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    deleteMasterDataField(key: number): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('MasterDataFields')}(${key})`;
        return this.http.delete<any>(url).pipe(
            catchError((error) => {
                console.error('[ERROR]: Delete MasterDataField Error:', error.message);
                return throwError(() => error);
            }),
        );
    }
    getOdata(odataQuery: string, params: any): Observable<any[]> {
        let url: string;
        if (params === null || params === undefined) {
            url = `${baseUrl}${odataQuery}`;
        } else {
            url = `${baseUrl}${odataQuery}?${params}`;
        }
        return this.http.get(url).pipe(
            map((response: any) => response),
            catchError((error) => {
                console.error('[ERROR]: Get OData Request Error:', error.message);
                throw error;
            }),
        );
    }

    // map((response: any) => (Object.prototype.hasOwnProperty.call(response, 'value') ? response.value : response)),

    getRelations(query: string): Observable<any> {
        const url = `${apiUrl}${query}`;
        return this.http.get(url).pipe(
            map((response: any) => response),
            catchError((err) => {
                console.error('[ERROR]: Get Relationship Request Error', err.message);
                throw err;
            }),
        );
    }

    getEntitySources(entityName: string, keyField: string, keyValue: number | string): Observable<{ value: any[] }> {
        const pluralEntity = pluralize(entityName);
        const expandNav = this.isConfigurationRoute()
            ? `Pre${entityName.charAt(0).toUpperCase() + entityName.slice(1)}Srcs`
            : entityName.charAt(0).toUpperCase() + entityName.slice(1) + 'Srcs';
        const filterVal = typeof keyValue === 'string' && isNaN(Number(keyValue)) ? `'${keyValue}'` : String(keyValue);
        const url = `${baseUrl}${this.entitySetForRequest(pluralEntity)}?$filter=${encodeURIComponent(keyField + ' eq ' + filterVal)}&$expand=${expandNav}($expand=${this.navProp('Src')})`;
        return this.http.get<{ value: any[] }>(url).pipe(
            map((res) => ({
                value: (res?.value ?? []).map((row: any) => this.normalizeExpandedRow(row)),
            })),
            catchError((err) => {
                console.error('[ERROR]: Get Entity Sources Error', err.message);
                throw err;
            }),
        );
    }
    getButtonList(): Observable<iButtonItem[]> {
        return of([
            { title: 'email', page: 'email', icon: 'email' },
            { title: 'share', page: 'share', icon: 'share' },
            { title: 'print', page: 'print', icon: 'print' },
        ]);
    }

    getParentData(masterTable: string, masterKey: string): Observable<any> {
        return this.http.get(`${apiUrl}link/${masterTable}/${masterKey}?relationship=Parent`);
    }

    getChildData(masterTable: string, masterKey: string): Observable<any> {
        return this.http.get(`${apiUrl}link/${masterTable}/${masterKey}?relationship=Child`);
    }

    getMergeData(masterTable: string, masterKey: string): Observable<any> {
        return this.http.get(`${apiUrl}merge/${masterTable}/${masterKey}`);
    }

    getMatchData(masterTable: string, masterKey: string): Observable<any> {
        return this.http.get(`${apiUrl}match/${masterTable}/${masterKey}`);
    }

    updateEntityRowData(entityName: string, key: any, rowData: any): Observable<any> {
        const url = `${baseUrl}${entityName}(${key})`;
        console.log('ODataService.updateEntityRowData called with:', url, rowData);

        return this.http.patch(url, rowData).pipe(
            map((response: any) => {
                console.log('ODataService.updateEntityRowData response:', response);
                return response;
            }),
            catchError((error) => {
                console.error('[ERROR]: Update Entity Row Data Error:', error);
                return throwError(() => new Error(`Error updating entity row data: ${error.message || 'Unknown error'}`));
            }),
        );
    }
    getSources(): Observable<any[]> {
        const url = `${baseUrl}${this.entitySetForRequest('Srcs')}`;
        console.log('ODataService.getSources called with:', url);

        return this.http.get<any>(url).pipe(
            map((response: any) => {
                console.log('ODataService.getSources response:', response);
                // OData typically returns data in a 'value' array
                return response.value || response;
            }),
            catchError((error) => {
                console.error('[ERROR]: Get Sources Error:', error);
                return throwError(() => new Error(`Error fetching sources: ${error.message || 'Unknown error'}`));
            }),
        );
    }

    /**
     * Create a new source
     * POST {{baseURL}}/OData/v1/Srcs
     * @param sourceData Object containing SrcNm and other source fields
     */
    createSource(sourceData: { SrcNm: string; [key: string]: any }, key?: number): Observable<any> {
        console.log('ODataService.createSource called with:', sourceData);

        return this.postWithEntityKey('Srcs', 'SrcTruid', sourceData, key).pipe(
            map((response: any) => {
                console.log('ODataService.createSource response:', response);
                return response;
            }),
            catchError((error) => {
                console.error('[ERROR]: Create Source Error:', error);
                return throwError(() => new Error(`Error creating source: ${error.message || 'Unknown error'}`));
            }),
        );
    }

    updateSource(key: number, sourceData: { SrcNm?: string; RcrdCrtUsrId?: string; SrcTruid?: number; SrcRcrdBusStatNm?: string; [key: string]: any }): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('Srcs')}(${key})`;
        console.log('ODataService.updateSource called with:', url, sourceData);

        return this.http.patch<any>(url, sourceData).pipe(
            map((response: any) => {
                console.log('ODataService.updateSource response:', response);
                return response;
            }),
            catchError((error) => {
                console.error('[ERROR]: Update Source Error:', error);
                return throwError(() => new Error(`Error updating source: ${error.message || 'Unknown error'}`));
            }),
        );
    }

    /**
     * Get all rule types from the RuleTypes endpoint
     * GET {{baseURL}}/OData/v1/RuleTypes
     */
    getRuleTypes(): Observable<any[]> {
        const url = `${baseUrl}${this.entitySetForRequest('DataFieldRuleTypes')}`;
        console.log('ODataService.getRuleTypes called with:', url);

        return this.http.get<any>(url).pipe(
            map((response: any) => {
                console.log('ODataService.getRuleTypes response:', response);
                return response.value || response;
            }),
            catchError((error) => {
                console.error('[ERROR]: Get Rule Types Error:', error);
                return throwError(() => new Error(`Error fetching rule types: ${error.message || 'Unknown error'}`));
            }),
        );
    }

    /**
     * Create a new rule type
     * POST {{baseURL}}/OData/v1/RuleTypes
     * @param ruleTypeData Object containing rule type fields
     */
    createRuleType(ruleTypeData: { DataFieldRuleType?: string; [key: string]: any }, key?: number): Observable<any> {
        console.log('ODataService.createRuleType called with:', ruleTypeData);

        return this.postWithEntityKey('DataFieldRuleTypes', 'DataFieldRuleTypeKey', ruleTypeData, key).pipe(
            map((response: any) => {
                console.log('ODataService.createRuleType response:', response);
                return response;
            }),
            catchError((error) => {
                console.error('[ERROR]: Create Rule Type Error:', error);
                return throwError(() => new Error(`Error creating rule type: ${error.message || 'Unknown error'}`));
            }),
        );
    }

    updateRuleType(key: number, ruleTypeData: { RuleType?: string; [key: string]: any }): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('DataFieldRuleTypes')}(${key})`;
        console.log('ODataService.updateRuleType called with:', url, ruleTypeData);

        return this.http.patch<any>(url, ruleTypeData).pipe(
            map((response: any) => {
                console.log('ODataService.updateRuleType response:', response);
                return response;
            }),
            catchError((error) => {
                console.error('[ERROR]: Update Rule Type Error:', error);
                return throwError(() => new Error(`Error updating rule type: ${error.message || 'Unknown error'}`));
            }),
        );
    }

    /**
     * Get all rules from the Rules endpoint
     * GET {{baseURL}}/OData/v1/Rules
     */
    getRules(): Observable<any[]> {
        const url = `${baseUrl}${this.entitySetForRequest('DataFieldRules')}`;
        console.log('ODataService.getRules called with:', url);

        return this.http.get<any>(url).pipe(
            map((response: any) => {
                console.log('ODataService.getRules response:', response);
                return response.value || response;
            }),
            catchError((error) => {
                console.error('[ERROR]: Get Rules Error:', error);
                return throwError(() => new Error(`Error fetching rules: ${error.message || 'Unknown error'}`));
            }),
        );
    }
    /**
     * Create a new rule
     * POST {{baseURL}}/OData/v1/Rules
     * @param ruleData Object containing rule fields
     */
    createRule(
        ruleData: {
            RuleName?: string;
            RuleTypeKey?: number;
            RuleFunction?: string;
            RuleInitialParameter?: string;
            RuleSubsequentParameter?: string;
            [key: string]: any;
        },
        key?: number,
    ): Observable<any> {
        console.log('ODataService.createRule called with:', ruleData);

        return this.postWithEntityKey('DataFieldRules', 'DataFieldRuleKey', ruleData, key).pipe(
            map((response: any) => {
                console.log('ODataService.createRule response:', response);
                return response;
            }),
            catchError((error) => {
                console.error('[ERROR]: Create Rule Error:', error);
                return throwError(() => new Error(`Error creating rule: ${error.message || 'Unknown error'}`));
            }),
        );
    }

    updateRule(
        key: number,
        ruleData: { RuleName?: string; RuleTypeKey?: number; RuleFunction?: string; RuleInitialParameter?: string; RuleSubsequentParameter?: string; [key: string]: any },
    ): Observable<any> {
        const url = `${baseUrl}${this.entitySetForRequest('DataFieldRules')}(${key})`;
        console.log('ODataService.updateRule called with:', url, ruleData);

        return this.http.patch<any>(url, ruleData).pipe(
            map((response: any) => {
                console.log('ODataService.updateRule response:', response);
                return response;
            }),
            catchError((error) => {
                console.error('[ERROR]: Update Rule Error:', error);
                return throwError(() => new Error(`Error updating rule: ${error.message || 'Unknown error'}`));
            }),
        );
    }

    getIngestMasterLinks(masterTableKey: number, masterDataFieldKeys: number[] = []): Observable<{ value: any[] }> {
        const entitySet = this.entitySetForRequest('IngestMasterLinks');
        const navUrl = `${baseUrl}${entitySet}?$filter=${this.navProp('MasterDataField')}/MasterTableKey eq ${masterTableKey}`;
        return this.http.get<any>(navUrl).pipe(
            map((res: any) => ({ value: res?.value ?? [] })),
            catchError(() => {
                const url = `${baseUrl}${entitySet}`;
                return this.http.get<any>(url).pipe(
                    map((res: any) => {
                        const all = res?.value ?? [];
                        if (masterDataFieldKeys.length === 0) {
                            return { value: all };
                        }
                        const keySet = new Set(masterDataFieldKeys);
                        return { value: all.filter((l: any) => keySet.has(l.MasterDataFieldKey)) };
                    }),
                );
            }),
            catchError((error) => {
                console.error('[ERROR]: Get IngestMasterLinks Error:', error.message);
                return throwError(() => error);
            }),
        );
    }
    executeODataBatch(operations: ODataBatchOperation[]): Observable<void> {
        if (operations.length === 0) {
            return of(undefined);
        }

        const batchBoundary = 'batch_boundary';
        const changesetBoundary = 'changeset_boundary';
        const mappedOperations = operations.map((op) => ({
            ...op,
            resource: this.mapBatchResource(op.resource),
        }));
        const body = this.buildODataBatchBody(mappedOperations, batchBoundary, changesetBoundary);
        const url = `${baseUrl}$batch`;
        const blob = new Blob([body], { type: `multipart/mixed; boundary=${batchBoundary}` });

        return this.http
            .post(url, blob, {
                headers: {
                    Accept: 'multipart/mixed',
                    'Content-Type': `multipart/mixed; boundary=${batchBoundary}`,
                },
                responseType: 'text',
            })
            .pipe(
                map((response) => {
                    this.assertBatchResponseOk(response);
                }),
                catchError((error) => {
                    console.error('[ERROR]: OData $batch Error:', error.message);
                    return throwError(() => error);
                }),
            );
    }

    private buildODataBatchBody(operations: ODataBatchOperation[], batchBoundary: string, changesetBoundary: string): string {
        const CRLF = '\r\n';
        const lines: string[] = [];

        lines.push(`--${batchBoundary}${CRLF}`);
        lines.push(`Content-Type: multipart/mixed; boundary=${changesetBoundary}${CRLF}`);
        lines.push(CRLF);

        operations.forEach((op, index) => {
            const contentId = op.contentId ?? index + 1;

            lines.push(`--${changesetBoundary}${CRLF}`);
            lines.push(`Content-Type: application/http${CRLF}`);
            lines.push(`Content-Transfer-Encoding: binary${CRLF}`);
            lines.push(`Content-ID: ${contentId}${CRLF}`);
            lines.push(CRLF);

            lines.push(`${op.method} ${op.resource} HTTP/1.1${CRLF}`);

            if (op.body != null && (op.method === 'POST' || op.method === 'PATCH')) {
                lines.push(`Content-Type: application/json${CRLF}`);
                lines.push(CRLF);
                lines.push(`${JSON.stringify(op.body)}${CRLF}`);
            } else {
                lines.push(CRLF);
                lines.push(CRLF);
            }
        });

        lines.push(`--${changesetBoundary}--${CRLF}`);
        lines.push(`--${batchBoundary}--${CRLF}`);

        return lines.join('');
    }
    private assertBatchResponseOk(response: string): void {
        const statusMatches = response.match(/HTTP\/1\.\d\s+(\d+)/g) ?? [];
        const failed = statusMatches.map((line) => Number(line.replace(/HTTP\/1\.\d\s+/, ''))).filter((code) => code < 200 || code >= 300);

        if (failed.length > 0) {
            throw new Error(`OData batch failed with status code(s): ${failed.join(', ')}`);
        }
    }

    getDataFieldRuleLinks(ingestMasterLinkKey: number): Observable<{ value: any[] }> {
        const url = `${baseUrl}${this.entitySetForRequest('DataFieldRuleLinks')}?$filter=IngestMasterLinkKey eq ${ingestMasterLinkKey}`;
        return this.http.get<any>(url).pipe(
            map((res: any) => ({ value: res?.value ?? [] })),
            catchError((error) => {
                console.error('[ERROR]: Get DataFieldRuleLinks Error:', error.message);
                return throwError(() => error);
            }),
        );
    }

    getDataFieldRuleLinksByLinkKeys(linkKeys: number[]): Observable<{ value: any[] }> {
        if (linkKeys.length === 0) {
            return of({ value: [] });
        }
        const filter = linkKeys.map((k) => `IngestMasterLinkKey eq ${k}`).join(' or ');
        const url = `${baseUrl}${this.entitySetForRequest('DataFieldRuleLinks')}?$filter=${filter}`;
        return this.http.get<any>(url).pipe(
            map((res: any) => ({ value: res?.value ?? [] })),
            catchError((error) => {
                console.error('[ERROR]: Get DataFieldRuleLinksByLinkKeys Error:', error.message);
                return throwError(() => error);
            }),
        );
    }
    /** Returns the highest numeric key in an OData entity set (0 when empty). */
    getMaxEntityKey(entitySet: string, keyField: string): Observable<number> {
        const resolvedEntitySet = this.entitySetForRequest(entitySet);
        const url = `${baseUrl}${resolvedEntitySet}` + `?$orderby=${encodeURIComponent(keyField + ' desc')}` + `&$top=1` + `&$select=${encodeURIComponent(keyField)}`;
        return this.http.get<any>(url).pipe(
            map((res: any) => {
                const rows = res?.value ?? [];
                return rows.length > 0 ? Number(rows[0][keyField] ?? 0) : 0;
            }),
            catchError((error) => {
                console.error(`[ERROR]: Get max ${keyField} for ${entitySet} Error:`, error.message);
                return throwError(() => error);
            }),
        );
    }

    private postWithEntityKey<T extends Record<string, any>>(entitySet: string, keyField: string, data: T, key?: number): Observable<any> {
        const assignKey = (nextKey: number) => {
            const url = `${baseUrl}${this.entitySetForRequest(entitySet)}`;
            return this.http.post<any>(url, { ...data, [keyField]: nextKey });
        };

        if (key != null) {
            return assignKey(key);
        }

        return this.getMaxEntityKey(entitySet, keyField).pipe(switchMap((maxKey) => assignKey(maxKey + 1)));
    }
}
